import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Skeleton, Tag, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { useAuth } from '../../contexts/AuthContext';
import { professorPortalService } from '../../services/api';
import '../../styles/ProfessorPortal.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

const STATUS_AULA_LABELS = {
  PREVISTA: { label: 'Prevista', color: 'blue' },
  REALIZADA: { label: 'Realizada', color: 'green' },
  CANCELADA: { label: 'Cancelada', color: 'red' },
};

const formatarHora = (valor) => (typeof valor === 'string' ? valor.slice(0, 5) : '--:--');

const formatarDataSelecionada = (data) => {
  const hoje = dayjs();
  if (data.isSame(hoje, 'day')) {
    return `Hoje, ${data.format('D [de] MMMM')}`;
  }
  const texto = data.format('dddd, D [de] MMMM');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const derivarSituacaoChamada = (encontro) => {
  if (encontro.status === 'CANCELADA') {
    return { label: 'Aula cancelada', botao: null };
  }
  if (encontro.chamada_completa === true) {
    return { label: 'Chamada concluída', botao: 'Ver chamada' };
  }
  if ((encontro.total_frequencias_informadas || 0) > 0) {
    return { label: 'Chamada em andamento', botao: 'Continuar chamada' };
  }
  return { label: 'Chamada não iniciada', botao: 'Fazer chamada' };
};

const resolverEstadoErro = (error) => {
  const status = error?.response?.status;
  if (status === 403) {
    return { mensagem: 'Você não possui acesso a esta área.', permiteTentarNovamente: false };
  }
  if (status === 409) {
    return {
      mensagem: 'Não foi possível identificar seu vínculo de professor. Entre em contato com a administração.',
      permiteTentarNovamente: false,
    };
  }
  return {
    mensagem: 'Não foi possível carregar suas aulas. Tente novamente.',
    permiteTentarNovamente: true,
  };
};

const ProfessorPortalHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dataSelecionada, setDataSelecionada] = useState(() => dayjs());
  const [encontros, setEncontros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const nomeUsuario = (
    user?.nome_completo || user?.nome || user?.full_name || user?.email || 'Professor'
  ).toString().trim();

  const carregarEncontros = useCallback(async (data) => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorPortalService.listarEncontros({
        data: data.format('YYYY-MM-DD'),
      });
      setEncontros(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setErro(resolverEstadoErro(error));
      setEncontros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarEncontros(dataSelecionada);
  }, [dataSelecionada, carregarEncontros]);

  const irParaDiaAnterior = () => setDataSelecionada((atual) => atual.subtract(1, 'day'));
  const irParaHoje = () => setDataSelecionada(dayjs());
  const irParaProximoDia = () => setDataSelecionada((atual) => atual.add(1, 'day'));
  const tentarNovamente = () => carregarEncontros(dataSelecionada);

  const abrirChamada = (encontroId) => {
    navigate(`/professor/encontros/${encontroId}/chamada`);
  };

  return (
    <div className="professor-portal-home">
      <div className="professor-portal-header">
        <Title level={2} className="professor-portal-greeting">
          Olá, {nomeUsuario}
        </Title>
        <Text type="secondary" className="professor-portal-subtitle">
          Suas aulas de hoje
        </Text>
      </div>

      <div className="professor-portal-date-control">
        <Button
          icon={<LeftOutlined />}
          aria-label="Dia anterior"
          onClick={irParaDiaAnterior}
        />
        <Text className="professor-portal-date-label">
          {formatarDataSelecionada(dataSelecionada)}
        </Text>
        <Button aria-label="Ir para hoje" onClick={irParaHoje}>
          Hoje
        </Button>
        <Button
          icon={<RightOutlined />}
          aria-label="Próximo dia"
          onClick={irParaProximoDia}
        />
      </div>

      {loading && (
        <div role="status" aria-live="polite">
          <Text type="secondary">Carregando suas aulas…</Text>
          <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: 12 }} />
          <Skeleton active paragraph={{ rows: 3 }} style={{ marginTop: 12 }} />
        </div>
      )}

      {!loading && erro && (
        <Card className="professor-encontro-card">
          <Text>{erro.mensagem}</Text>
          {erro.permiteTentarNovamente && (
            <div>
              <Button
                type="primary"
                className="professor-portal-retry-btn"
                onClick={tentarNovamente}
              >
                Tentar novamente
              </Button>
            </div>
          )}
        </Card>
      )}

      {!loading && !erro && encontros.length === 0 && (
        <Empty description="Você não possui aulas programadas para esta data." />
      )}

      {!loading && !erro && encontros.length > 0 && (
        <div className="professor-portal-encontros-grid">
          {encontros.map((encontro) => {
            const statusAula = STATUS_AULA_LABELS[encontro.status] || {
              label: encontro.status,
              color: 'default',
            };
            const situacaoChamada = derivarSituacaoChamada(encontro);
            const temFrequenciasInformadas = (encontro.total_frequencias_informadas || 0) > 0;

            return (
              <Card key={encontro.encontro_id} className="professor-encontro-card">
                <Text className="professor-encontro-horario">
                  {formatarHora(encontro.hora_inicio)}–{formatarHora(encontro.hora_fim)}
                </Text>
                <Text className="professor-encontro-turma">{encontro.nome_turma}</Text>
                <div className="professor-encontro-meta">
                  {encontro.nome_unidade && (
                    <Text type="secondary">{encontro.nome_unidade}</Text>
                  )}
                  <Text type="secondary">
                    {encontro.total_alunos_elegiveis} aluno(s)
                  </Text>
                  <Tag color={statusAula.color}>{statusAula.label}</Tag>
                </div>

                {temFrequenciasInformadas && (
                  <Text type="secondary">
                    Presentes: {encontro.quantidade_presentes} · Ausentes: {encontro.quantidade_ausentes} · Justificadas: {encontro.quantidade_justificadas}
                  </Text>
                )}

                <div className="professor-encontro-chamada-status">
                  <Text strong>Chamada: {situacaoChamada.label}</Text>
                </div>

                {situacaoChamada.botao && (
                  <div className="professor-encontro-acao">
                    <Button type="primary" onClick={() => abrirChamada(encontro.encontro_id)}>
                      {situacaoChamada.botao}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfessorPortalHome;
